import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Save,
  Building2,
  Image as ImageIcon,
  Trash2,
  UploadCloud,
  User as UserIcon,
  KeyRound,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const FIELDS = [
  { key: "name", label: "Company Name", required: true, placeholder: "e.g. ABC Pvt Ltd" },
  { key: "address_line1", label: "Address Line 1", required: true, placeholder: "Street / Plot / House no." },
  { key: "address_line2", label: "Address Line 2", placeholder: "Area / Landmark (optional)" },
  { key: "city", label: "City", required: true, placeholder: "e.g. Mumbai" },
  { key: "state", label: "State", required: true, placeholder: "e.g. Maharashtra" },
  { key: "pin", label: "PIN Code", required: true, placeholder: "6-digit" },
  { key: "gstin", label: "GSTIN", required: true, placeholder: "15-char GSTIN", mono: true },
  { key: "pan", label: "PAN", placeholder: "10-char PAN", mono: true },
  { key: "state_code", label: "State Code", placeholder: "Auto-filled from GSTIN", mono: true },
  { key: "mobile", label: "Mobile", required: true, placeholder: "10-digit" },
  { key: "email", label: "Email", required: true, placeholder: "billing@yourco.com" },
];

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    api.get("/settings/seller").then(({ data }) => setProfile(data));
  }, []);

  if (!profile) return <div className="p-8 text-zinc-500">Loading…</div>;

  const update = (k, v) => {
    setProfile((p) => {
      const next = { ...p, [k]: v };
      if (k === "gstin" && v && !p.state_code) {
        next.state_code = v.slice(0, 2);
      }
      return next;
    });
  };

  const save = async () => {
    const missing = FIELDS.filter((f) => f.required && !(profile[f.key] || "").trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/seller", profile);
      setProfile(data);
      toast.success("Seller profile saved — new invoices will use these details");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFile = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("Logo must be PNG, JPG or WEBP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setLogoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("position", profile.logo_position || "top-left");
    try {
      const { data } = await api.post("/settings/seller/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data);
      toast.success("Logo uploaded — it will appear on new invoice PDFs");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.delete("/settings/seller/logo");
      setProfile(data);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove logo");
    }
  };

  const setLogoPosition = (pos) => {
    setProfile((p) => ({ ...p, logo_position: pos }));
  };

  const toggleLogoEnabled = (val) => {
    setProfile((p) => ({ ...p, logo_enabled: !!val }));
  };

  const hasLogo = !!profile.logo_data_url;

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Settings</div>
        <h1 className="text-4xl font-bold tracking-tight" data-testid="settings-heading">
          Settings
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-xl">
          Your seller profile, logo and account.
        </p>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="mb-5">
          <TabsTrigger value="company" data-testid="tab-company">
            <Building2 size={14} className="mr-1" /> Company
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">
            <UserIcon size={14} className="mr-1" /> Account
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="space-y-0">
          <div className="flex justify-end mb-3">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={save}
              disabled={saving}
              data-testid="settings-save-btn"
            >
              <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Profile"}
            </Button>
          </div>

          <Card className="p-6">
            <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-5">
              <Building2 size={18} className="text-blue-600" />
              <div className="text-sm font-semibold">Company Details</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.key === "address_line1" || f.key === "address_line2" ? "col-span-2" : ""}>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
                    {f.label}
                    {f.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Input
                    value={profile[f.key] || ""}
                    onChange={(e) => update(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className={`mt-1 h-9 text-sm ${f.mono ? "mono" : ""}`}
                    data-testid={`seller-${f.key.replace(/_/g, "-")}`}
                  />
                </div>
              ))}
            </div>
          </Card>

      {/* Logo card */}
      <Card className="p-6 mt-4" data-testid="logo-card">
        <div className="flex items-center justify-between pb-3 border-b border-zinc-100 mb-5">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-blue-600" />
            <div className="text-sm font-semibold">Company Logo on Invoices</div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="logo-enabled" className="text-xs text-zinc-500">
              {profile.logo_enabled ? "Enabled" : "Disabled"}
            </Label>
            <Switch
              id="logo-enabled"
              checked={!!profile.logo_enabled}
              onCheckedChange={toggleLogoEnabled}
              disabled={!hasLogo}
              data-testid="logo-enabled-switch"
            />
          </div>
        </div>

        <div className="grid grid-cols-5 gap-6">
          {/* Preview */}
          <div className="col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Preview</Label>
            <div className="mt-2 h-32 rounded-md border border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
              {hasLogo ? (
                <img
                  src={profile.logo_data_url}
                  alt="Company logo"
                  className="max-h-28 max-w-full object-contain"
                  data-testid="logo-preview-img"
                />
              ) : (
                <div className="text-xs text-zinc-400 text-center px-3">
                  No logo yet.<br />Upload a PNG, JPG or WEBP (≤2 MB).
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
                data-testid="logo-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                data-testid="logo-upload-btn"
              >
                <UploadCloud size={14} className="mr-1" />
                {logoUploading ? "Uploading…" : hasLogo ? "Replace Logo" : "Upload Logo"}
              </Button>
              {hasLogo && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeLogo}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="logo-remove-btn"
                >
                  <Trash2 size={14} className="mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>

          {/* Position */}
          <div className="col-span-3">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
              Position on Invoice
            </Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {[
                { k: "top-left", label: "Top Left" },
                { k: "top-center", label: "Top Center" },
                { k: "top-right", label: "Top Right" },
              ].map((opt) => {
                const active = (profile.logo_position || "top-left") === opt.k;
                return (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setLogoPosition(opt.k)}
                    className={`rounded-md border p-3 text-left transition ${
                      active
                        ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100"
                        : "border-zinc-200 hover:border-zinc-300 bg-white"
                    }`}
                    data-testid={`logo-position-${opt.k}`}
                  >
                    <div className="h-10 rounded border border-zinc-200 bg-white flex items-center p-1 mb-2">
                      <div
                        className={`h-6 w-6 rounded-sm bg-blue-600 ${
                          opt.k === "top-center"
                            ? "mx-auto"
                            : opt.k === "top-right"
                            ? "ml-auto"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="text-xs font-medium">{opt.label}</div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              The logo renders at ~90px tall (aspect-preserved). Enable or disable it anytime —
              future invoice PDFs will reflect your choice. Remember to click <b>Save Profile</b>
              {" "}after changing the position or enable state.
            </p>
          </div>
        </div>
      </Card>

          <div className="flex justify-end mt-4">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={save}
              disabled={saving}
              data-testid="settings-save-bottom"
            >
              <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Profile"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="account" className="space-y-4">
          <AccountTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function AccountTab() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Profile edit state
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  // Password change state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  // Delete account state
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const createdAt = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name });
      await refreshUser();
      toast.success("Name updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save name");
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async () => {
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    setChangingPw(true);
    try {
      await api.post(
        "/auth/change-password",
        { current_password: currentPw, new_password: newPw },
        { _skip401Logout: true },
      );
      toast.success("Password updated — stay logged in");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPw(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
      toast.error("Email confirmation does not match");
      return;
    }
    if (!confirmPassword) {
      toast.error("Enter your password to confirm");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me", {
        data: { password: confirmPassword },
        _skip401Logout: true,
      });
      toast.success("Account deleted");
      setDeleteOpen(false);
      await logout();
      navigate("/signup", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Profile info */}
      <Card className="p-6" data-testid="account-profile-card">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-5">
          <UserIcon size={18} className="text-blue-600" />
          <div className="text-sm font-semibold">Account Info</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Email</Label>
            <Input
              value={user?.email || ""}
              readOnly
              disabled
              className="mt-1 h-9 text-sm mono bg-zinc-50"
              data-testid="account-email"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
              Member since
            </Label>
            <Input
              value={createdAt}
              readOnly
              disabled
              className="mt-1 h-9 text-sm bg-zinc-50"
              data-testid="account-created-at"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
              Display Name
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How your name appears across the app"
                className="h-9 text-sm"
                data-testid="account-name"
              />
              <Button
                onClick={saveName}
                disabled={savingName || name === (user?.name || "")}
                className="h-9"
                data-testid="account-save-name"
              >
                {savingName ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Change password */}
      <Card className="p-6" data-testid="account-password-card">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-5">
          <KeyRound size={18} className="text-blue-600" />
          <div className="text-sm font-semibold">Change Password</div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
              Current Password
            </Label>
            <Input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="mt-1 h-9 text-sm"
              autoComplete="current-password"
              data-testid="account-current-password"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
              New Password
            </Label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1 h-9 text-sm"
              autoComplete="new-password"
              placeholder="min 6 characters"
              data-testid="account-new-password"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
              Confirm New
            </Label>
            <Input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mt-1 h-9 text-sm"
              autoComplete="new-password"
              data-testid="account-confirm-password"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button
            onClick={changePassword}
            disabled={changingPw || !currentPw || !newPw || !confirmPw}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="account-change-password-btn"
          >
            {changingPw ? <Loader2 size={14} className="animate-spin mr-1" /> : <KeyRound size={14} className="mr-1" />}
            {changingPw ? "Saving…" : "Change Password"}
          </Button>
        </div>
      </Card>

      {/* Danger zone */}
      <Card className="p-6 border-red-200 bg-red-50/40" data-testid="account-danger-card">
        <div className="flex items-center gap-2 pb-3 border-b border-red-200 mb-5">
          <AlertTriangle size={18} className="text-red-600" />
          <div className="text-sm font-semibold text-red-900">Danger Zone</div>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="text-xs text-red-800 max-w-md leading-relaxed">
            <b>Delete this account.</b> Every invoice, buyer, seller profile, logo
            and bulk-job you created will be <b>permanently removed</b>. This
            cannot be undone.
          </div>
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="account-delete-open">
                <Trash2 size={14} className="mr-1" /> Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="account-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Permanently delete your account?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every invoice, buyer, seller profile, logo and bulk-job tied to{" "}
                  <b>{user?.email}</b> will be deleted. There is no recovery.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
                    Type your email to confirm
                  </Label>
                  <Input
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder={user?.email}
                    className="mt-1 h-9 text-sm mono"
                    data-testid="account-delete-email"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
                    Password
                  </Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 h-9 text-sm"
                    data-testid="account-delete-password"
                  />
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    setConfirmEmail("");
                    setConfirmPassword("");
                  }}
                  data-testid="account-delete-cancel"
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    deleteAccount();
                  }}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={deleting}
                  data-testid="account-delete-confirm"
                >
                  {deleting ? "Deleting…" : "Delete Permanently"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
    </>
  );
}
